import * as React from "react";
import { useCallback, useId, useMemo, useRef, useState } from "react";
import { RichTreeViewPro } from "@mui/x-tree-view-pro/RichTreeViewPro";
import { type TreeViewBaseItem, type TreeItemProps } from "@mui/x-tree-view-pro";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Popover,
  styled,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "~/common/mui";
import { BaseTreeItem } from "./BaseTreeItem";

// ────────────────────────────────────────────
// Public types
// ────────────────────────────────────────────

export interface MultiLevelSelectItem extends TreeViewBaseItem {
  children?: MultiLevelSelectItem[];
  [key: string]: unknown;
}

interface BaseProps {
  className?: string;
  label?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  sx?: SxProps<Theme>;
  items: MultiLevelSelectItem[];
  slots?: {
    item?: React.JSXElementConstructor<TreeItemProps>;
  };
}

interface SingleSelectProps extends BaseProps {
  multiple?: false;
  value: string;
  onChange: (newValue: string) => void;
  renderValue?: (selectedId: string, items: MultiLevelSelectItem[]) => string;
  showSelectAll?: never;
  selectAllLabel?: never;
}

export interface MultiSelectProps extends BaseProps {
  multiple: true;
  value: string[];
  onChange: (newValue: string[]) => void;
  renderValue?: (selectedIds: string[], items: MultiLevelSelectItem[]) => string;
  showSelectAll?: boolean;
  selectAllLabel?: string;
}

export type MultiLevelSelectProps = SingleSelectProps | MultiSelectProps;

// ────────────────────────────────────────────
// Tree helpers
// ────────────────────────────────────────────

function getAllLeafIds(items: MultiLevelSelectItem[]): string[] {
  const ids: string[] = [];
  function walk(node: MultiLevelSelectItem) {
    if (!node.children?.length) {
      ids.push(node.id);
    } else {
      node.children.forEach(walk);
    }
  }
  items.forEach(walk);
  return ids;
}

function getLeafIds(items: MultiLevelSelectItem[], selectedIds: string[]): string[] {
  const selected = new Set(selectedIds);
  const ids: string[] = [];
  function walk(node: MultiLevelSelectItem) {
    if (!node.children?.length) {
      if (selected.has(node.id)) {
        ids.push(node.id);
      }
    } else {
      node.children.forEach(walk);
    }
  }
  items.forEach(walk);
  return ids;
}

function computeFullSelection(items: MultiLevelSelectItem[], leafIds: Set<string>): string[] {
  const result: string[] = [];
  function walk(node: MultiLevelSelectItem): boolean {
    if (!node.children?.length) {
      const isSelected = leafIds.has(node.id);
      if (isSelected) {
        result.push(node.id);
      }
      return isSelected;
    }
    const allSelected = node.children.map(walk).every(Boolean);
    if (allSelected) {
      result.push(node.id);
    }
    return allSelected;
  }
  items.forEach(walk);
  return result;
}

function findItem(items: MultiLevelSelectItem[], id: string): MultiLevelSelectItem | undefined {
  for (const item of items) {
    if (item.id === id) {
      return item;
    }
    if (item.children) {
      const found = findItem(item.children, id);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
}

// ────────────────────────────────────────────
// Styled components
// ────────────────────────────────────────────

const DropdownIcon = styled(ArrowDropDownIcon, {
  shouldForwardProp: (prop) => prop !== "open",
})<{ open: boolean }>(({ theme, open }) => ({
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
  transform: open ? "rotate(180deg)" : "rotate(0deg)",
  color: theme.palette.action.active,
}));

const TreeContainer = styled("div")(({ theme }) => ({
  padding: theme.spacing(1, 0),
  maxHeight: 320,
  overflowY: "auto",
}));

const SelectAllRow = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const DefaultTreeItem = React.forwardRef<HTMLLIElement, React.ComponentProps<typeof BaseTreeItem>>(
  function DefaultTreeItem(props, ref) {
    return (
      <BaseTreeItem
        {...props}
        ref={ref}
        renderContent={(label) => <Typography variant="bodyLarge">{label}</Typography>}
      />
    );
  },
);

// ────────────────────────────────────────────
// Component
// ────────────────────────────────────────────

export const MultiLevelSelect = (props: MultiLevelSelectProps) => {
  const { label, fullWidth, disabled, error, helperText, sx, items, slots, className } = props;
  const { multiple, value, onChange, renderValue } = props as MultiSelectProps & SingleSelectProps;

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const treeId = useId();
  const comboboxId = useId();

  // ── Derived state for multi-select ──
  const allLeafIds = useMemo(() => getAllLeafIds(items), [items]);

  const internalSelectedItems: string[] = useMemo(() => {
    if (multiple) {
      return computeFullSelection(items, new Set(value as string[]));
    }
    return value ? [value as string] : [];
  }, [multiple, value, items]);

  // ── Select All state (multi only) ──
  const allSelected = multiple ? allLeafIds.every((id) => (value as string[]).includes(id)) : false;
  const someSelected = multiple ? (value as string[]).length > 0 && !allSelected : false;

  // ── Display value ──
  const displayValue = useMemo(() => {
    if (multiple) {
      const multiValue = value as string[];
      if (renderValue) {
        return (renderValue as MultiSelectProps["renderValue"])!(multiValue, items);
      }
      if (multiValue.length === 0) {
        return "";
      }
      return `${multiValue.length} selected`;
    }
    const singleValue = value as string;
    if (renderValue) {
      return (renderValue as SingleSelectProps["renderValue"])!(singleValue, items);
    }
    if (!singleValue) {
      return "";
    }
    return findItem(items, singleValue)?.label ?? singleValue;
  }, [multiple, value, renderValue, items]);

  // ── Handlers ──
  const handleOpen = useCallback(() => {
    if (!disabled) {
      setOpen(true);
    }
  }, [disabled]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleMultiChange = useCallback(
    (_event: React.SyntheticEvent | null, newSelectedItems: string[]) => {
      const leafOnly = getLeafIds(items, newSelectedItems);
      (onChange as MultiSelectProps["onChange"])(leafOnly);
    },
    [items, onChange],
  );

  const handleSingleChange = useCallback(
    (_event: React.SyntheticEvent | null, newSelectedItem: string | null) => {
      (onChange as SingleSelectProps["onChange"])(newSelectedItem ?? "");
      setOpen(false);
    },
    [onChange],
  );

  const handleSelectAll = useCallback(() => {
    if (!multiple) {
      return;
    }
    (onChange as MultiSelectProps["onChange"])(allSelected ? [] : allLeafIds);
  }, [multiple, onChange, allSelected, allLeafIds]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        handleOpen();
      }
    },
    [handleOpen],
  );

  // ── Render ──
  const hasValue = multiple ? (value as string[]).length > 0 : !!value;

  return (
    <>
      <TextField
        className={className}
        ref={anchorRef}
        id={comboboxId}
        value={displayValue}
        label={label}
        fullWidth={fullWidth}
        disabled={disabled}
        error={error}
        helperText={helperText}
        sx={sx}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        slotProps={{
          inputLabel: {
            shrink: hasValue || open,
          },
          htmlInput: {
            role: "combobox",
            "aria-expanded": open,
            "aria-haspopup": "tree",
            "aria-owns": open ? treeId : undefined,
            readOnly: true,
            style: { cursor: disabled ? "default" : "pointer" },
            tabIndex: 0,
          },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <DropdownIcon open={open} />
              </InputAdornment>
            ),
            sx: { cursor: disabled ? "default" : "pointer" },
          },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          paper: {
            sx: (theme) => ({
              width: anchorRef.current?.offsetWidth,
              mt: 0.5,
              backgroundColor: theme.palette.surfaceContainer.low,
              boxShadow: theme.shadows[2],
            }),
          },
        }}
      >
        {multiple && (props as MultiSelectProps).showSelectAll && (
          <SelectAllRow>
            <FormControlLabel
              control={<Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleSelectAll} />}
              label={(props as MultiSelectProps).selectAllLabel ?? "All"}
            />
          </SelectAllRow>
        )}
        <TreeContainer id={treeId} role="tree" aria-label={label}>
          {multiple ? (
            <RichTreeViewPro<MultiLevelSelectItem, true>
              items={items}
              multiSelect
              checkboxSelection
              selectedItems={internalSelectedItems}
              onSelectedItemsChange={handleMultiChange}
              selectionPropagation={{ parents: true, descendants: true }}
              itemChildrenIndentation={32}
              slots={{ item: slots?.item ?? DefaultTreeItem }}
            />
          ) : (
            <RichTreeViewPro<MultiLevelSelectItem, false>
              items={items}
              selectedItems={internalSelectedItems[0] ?? null}
              onSelectedItemsChange={handleSingleChange}
              itemChildrenIndentation={32}
              slots={{ item: slots?.item ?? DefaultTreeItem }}
            />
          )}
        </TreeContainer>
      </Popover>
    </>
  );
};
